<template>
    <div id="app">
        <div class="absolute top-0 z-50 inset-x-0 w-full flex justify-center mt-5">
            <div
                :class="
                    `${
                        isError
                            ? 'bg-red-pale text-red-dark'
                            : messageType === 'warning'
                            ? 'bg-yellow-pale text-yellow-dark'
                            : 'bg-green-pale text-green-dark'
                    } py-2 px-5 rounded-lg text-sm transform duration-500 ${
                        message ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
                    } `
                "
            >
                <div v-html="message"></div>
            </div>
        </div>
        <router-view />
        <processing :shouldShow="processing" />
        <success :shouldShow="success" @go-to-dashboard="goToDashboard" />
        <failed :shouldShow="failed" @retry="failed = false" @go-to-dashboard="goToDashboard" :message="errorMessage" />
        <pending
            :shouldShow="pending && !processing"
            :reference="transaction_details.reference_code"
            @close="pending = false"
        />
        <Paycode :showModal="Object.entries(paycode).length !== 0" :data="paycode" @close="paycode = {}" />
        <Education :showModal="Object.entries(education).length !== 0" :data="education" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Processing from '@/components/modals/Processing'
import Success from '@/components/modals/Success'
import Failed from '@/components/modals/Failed'
import Pending from '@/components/modals/Pending'
import Paycode from '@/components/modals/Paycode'
import Education from '@/components/modals/Education'

export default {
    name: 'App',
    metaInfo: {
        // if no subcomponents specify a metaInfo.title, this title will be used
        title: 'Dashboard',
        // all titles will be injected into this template
        titleTemplate: '%s | BillerPay',
    },

    components: {
        Processing,
        Success,
        Pending,
        Failed,
        Paycode,
        Education,
    },

    data() {
        return {
            failed: false,
            success: false,
            pending: false,
            errorMessage: '',
            paycode: {},
            education: {},
        }
    },
    computed: {
        ...mapGetters('Message', {
            message: 'GET_MESSAGE',
            isError: 'GET_MESSAGE_TYPE',
            messageType: 'GET_WARNING',
        }),

        ...mapGetters('Transaction', {
            processing: 'GET_LOADING_TRANSACTION_STATUS',
            transaction_details: 'GET_TRANSACTION_DETAILS',
        }),

        token: function() {
            return localStorage.getItem('token')
        },
    },

    watch: {
        transaction_details: {
            handler(val) {
                if (val.status === 'declined') {
                    this.failed = true
                    this.pending = false
                    this.success = false
                } else if (val.status === 'confirmed') {
                    this.failed = false
                    this.pending = false
                    this.success = true
                } else if (val.status === 'pending') {
                    this.failed = false
                    this.success = false
                    this.pending = true
                }
            },
            deep: true,
        },

        token: function(newValue) {
            if (newValue) {
                this.connectSocket(newValue)
            }
        },
    },

    mounted() {
        if (this.token) {
            this.connectSocket(this.token)
        }
    },

    methods: {
        connectSocket(token) {
            window.Echo.private(`transaction.${token}`).listen('TransactionUpdate', (e) => {
                console.log(e)
                if (e.transactionStatus.status === 'confirmed') {
                    if (e.transactionMeta.type === 'paycode_payment') {
                        if (e.transactionMeta.paycode_response) {
                            this.paycode = e.transactionMeta.paycode_response
                        } else {
                            this.success = true
                        }
                    } else if (e.transactionMeta.type === 'education_payment') {
                        this.education = e.transactionMeta.education_response
                    } else {
                        this.success = true
                    }
                } else if (e.transactionStatus.status === 'declined') {
                    this.success = false
                    this.failed = true
                    this.errorMessage = e.message
                }
                if (e.transactionStatus.status !== 'pending') {
                    this.setNotification({
                        type: this.getNotificationType(e.transactionStatus.status),
                        title: this.getTitle(e.transactionStatus.status),
                        body:
                            e.transactionStatus.status === 'confirmed' ? this.getMessage(e.transactionMeta) : e.message,
                        created_at: new Date(),
                        seen: false,
                    })
                }
            })
        },

        getTitle(type) {
            if (type === 'confirmed') {
                return 'Your payment has been confirmed'
            } else if (type === 'declined') {
                return 'Your transaction has failed'
            }
        },

        getNotificationType(type) {
            if (type === 'confimed') {
                return 2
            } else if (type === 'declined') {
                return 3
            }
        },

        getMessage(meta) {
            let message

            switch (meta.type) {
                case 'airtime_payment':
                    message = `<b>${meta.phone_number}</b> has been credited with a total amount of <b>₦${meta.amount}</b>`
                    break
                case 'electric_payment':
                    message = `You have successfully made payment to <b>${meta.product_name}</b>`
                    break
                case 'fund_transfer':
                    message = `You have successfully transferred <b>₦${meta.amount}</b> to <b>${meta.account_name}</b> at <b>${meta.bank}</b>`
                    break
                case 'paycode_payment':
                    message = `Your request for paycode through <b>${meta.withdrawal_channel}</b> for <b>₦${meta.amount}</b> was successful`
                    break
                case 'education_payment':
                    message = `You have successfully paid <b>₦${meta.amount}</b> for <b>${meta.name}</b>`
                    break
                case 'data_payment':
                    message = `You have successfully bought data worth of <b>₦${meta.amount}</b> for <b>${meta.phone_number}</b>`
                    break
                case 'cable_payment':
                    message = `You have successfully made a cable payment of <b>₦${meta.amount}</b> to <b>${meta.account_name}</b>`
                    break
                default:
                    message = 'Your transaction was successful'
            }

            return message + '<br /> Thank you for using biller pay <span role="img" aria-label="love">💜</span>'
        },

        goToDashboard() {
            this.success = false
            this.failed = false
            this.$router.push('/bills')
        },
    },
}
</script>

<style>
input,
button {
    outline: 0;
}
</style>
